import { useState } from "react";
import { useLang } from "@/context/LanguageContext";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Clock, MapPin, Trophy } from "lucide-react";

function ScheduleBlock({ schedule, testIdPrefix }) {
  return (
    <div className="bg-[#0A0A0A] border border-[#262626] overflow-hidden">
      {schedule.map((slot, i) => (
        <div
          key={slot.time}
          className={`flex items-center gap-6 px-6 py-4 ${i < schedule.length - 1 ? "border-b border-[#262626]" : ""} hover:bg-[#111111] transition-colors`}
          data-testid={`${testIdPrefix}-${i}`}
        >
          <span className="font-heading text-sm text-[#FF3B30] w-32 shrink-0">
            {slot.time}
          </span>
          <span className="text-sm text-[#A3A3A3] font-body">
            {slot.activity}
          </span>
        </div>
      ))}
    </div>
  );
}

export default function Programme() {
  const { t } = useLang();
  const [activeSchedule, setActiveSchedule] = useState("A");

  return (
    <section
      id="programme"
      data-testid="programme-section"
      className="py-24 sm:py-32 bg-[#111111]"
    >
      <div className="max-w-6xl mx-auto px-6">
        {/* Section Label */}
        <div className="flex items-center gap-3 mb-4">
          <div className="w-8 h-[2px] bg-[#FF3B30]" />
          <span className="text-xs uppercase tracking-[0.3em] text-[#A3A3A3] font-body">
            {t.programme.label}
          </span>
        </div>

        <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl uppercase tracking-tight text-white mb-4">
          {t.programme.title}
        </h2>

        <p className="text-sm sm:text-base text-[#A3A3A3] font-body leading-relaxed max-w-3xl mb-12">
          {t.programme.intro}
        </p>

        {/* Schedule Toggle */}
        <div className="flex items-center gap-2 mb-6">
          <Clock className="w-5 h-5 text-[#FF3B30]" />
          <div className="flex gap-0">
            <button
              data-testid="schedule-toggle-a"
              onClick={() => setActiveSchedule("A")}
              className={`px-5 py-2.5 font-heading text-xs uppercase tracking-wider border border-[#262626] transition-colors ${activeSchedule === "A" ? "bg-[#FF3B30] text-white border-[#FF3B30]" : "text-[#A3A3A3] hover:text-white"}`}
            >
              {t.programme.scheduleALabel}
            </button>
            <button
              data-testid="schedule-toggle-b"
              onClick={() => setActiveSchedule("B")}
              className={`px-5 py-2.5 font-heading text-xs uppercase tracking-wider border border-[#262626] border-l-0 transition-colors ${activeSchedule === "B" ? "bg-[#FF3B30] text-white border-[#FF3B30]" : "text-[#A3A3A3] hover:text-white"}`}
            >
              {t.programme.scheduleBLabel}
            </button>
          </div>
        </div>

        <div className="mb-20" data-testid="daily-schedule">
          {activeSchedule === "A" ? (
            <ScheduleBlock schedule={t.programme.scheduleA} testIdPrefix="schedule-a-slot" />
          ) : (
            <ScheduleBlock schedule={t.programme.scheduleB} testIdPrefix="schedule-b-slot" />
          )}
        </div>

        {/* Competition & Travel */}
        <div className="flex items-center gap-2 mb-4">
          <Trophy className="w-5 h-5 text-[#FF3B30]" />
          <h3 className="font-heading text-2xl sm:text-3xl uppercase tracking-wider text-white">
            {t.programme.travelTitle}
          </h3>
        </div>

        <p className="text-sm sm:text-base text-[#A3A3A3] font-body leading-relaxed max-w-3xl mb-8">
          {t.programme.travelIntro}
        </p>

        {/* Events Table */}
        <div className="bg-[#0A0A0A] border border-[#262626] overflow-hidden mb-6" data-testid="travel-events">
          <Table>
            <TableHeader>
              <TableRow className="border-[#262626] hover:bg-transparent bg-[#111111]">
                <TableHead className="text-xs uppercase tracking-[0.15em] text-[#A3A3A3] font-heading py-3 px-6">
                  Event
                </TableHead>
                <TableHead className="text-xs uppercase tracking-[0.15em] text-[#A3A3A3] font-heading py-3 px-6 hidden sm:table-cell">
                  Location
                </TableHead>
                <TableHead className="text-xs uppercase tracking-[0.15em] text-[#A3A3A3] font-heading py-3 px-6">
                  Discipline
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {t.programme.travelEvents.map((evt, i) => (
                <TableRow key={evt.event} className="border-[#262626] hover:bg-[#111111]" data-testid={`travel-event-${i}`}>
                  <TableCell className="text-sm text-white font-body font-semibold py-4 px-6">
                    {evt.event}
                  </TableCell>
                  <TableCell className="text-sm text-[#A3A3A3] font-body py-4 px-6 hidden sm:table-cell">
                    <div className="flex items-center gap-1.5">
                      <MapPin className="w-3 h-3 text-[#FF3B30]" />
                      {evt.location}
                    </div>
                  </TableCell>
                  <TableCell className="py-4 px-6">
                    <span className="text-xs uppercase tracking-[0.1em] text-[#FF3B30] font-heading bg-[#FF3B30]/10 px-2 py-1">
                      {evt.discipline}
                    </span>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        {/* Travel note */}
        <p className="text-xs text-[#555] font-body leading-relaxed">
          {t.programme.travelNote}
        </p>
      </div>
    </section>
  );
}
